import { useEffect, useMemo, useState } from 'react';
import cm from 'classnames';

import { FileStatus, FileType, ProjectFile } from 'services/file.service';
import { useAppDispatch, useAppSelector } from 'store/store-hooks';

import {
  isFileStatusCompleted,
  prettyBitrate,
  prettyBytes,
  prettyDate,
  prettyDuration,
  prettyFrameRate,
  prettyResolution,
} from 'components/app/_helpers/_app.helper';
import { IntroKeys } from 'components/app/intro/intro-keys';
import { setRemovingInstance, selectHelpMode } from 'components/app/_mediator/app-data.slice';
import { FileUplId, UploadingFile } from 'components/app/_mediator/app-files-upload.slice';

import { setSelectedFile } from '../_mediator/projects.slice';
import { getFileSortCb, PROJECTS_FILES_HEADERS } from '../_mediator/projects.params';
import { mapFileStatusToLabel, sortProjectEntities } from '../_mediator/projects.helper';
import { useProjectsSearch } from '../use-projects-search';

import { ReactComponent as PlusIcon } from 'assets/ui/plus.svg';
import { ReactComponent as TrashIcon } from 'assets/ui/trash.svg';

import { Loader } from 'components/UI/loader/loader';
import { Tooltip } from 'components/UI/tooltip/tooltip';

import { PickerBase } from '../files-picker/picker-base';
import { PickerDropZone } from '../files-picker/picker-drop-zone';
import { PlaceholderFiles } from '../placeholders/placeholder-files';

import { HeaderAction } from '../elements/header-action';
import { HeaderSearch } from '../elements/header-search';
import { DateCalendar } from '../elements/date-calendar';

import { ProjectsTable } from './elements/projects-table';
import { SectionHeader } from './elements/section-header';
import { ThumbFile } from './elements/thumb-table';
import { Section } from './elements/section';

import styles from './_sections.module.scss';

export const SectionFiles = () => {
  const { searchValue, updateSearchValue, getFilteredList, clearSearchValue } = useProjectsSearch('filesSearchFilter');
  const { selectedProject, filesTableState, selectedFile, filesTableSort } = useAppSelector((state) => state.projects);
  const { projects, removingInstance } = useAppSelector((state) => state.app__data);
  const uploadingFiles = useAppSelector((state) => state.app__filesUpload.uploadingFiles);
  const files = useAppSelector((state) => state.app__files.files);
  const dispatch = useAppDispatch();

  const [selected, setSelected] = useState<FileId | FileUplId | null>(null);

  const filteredFiles = useMemo(() => {
    return getFilteredList<ProjectFile>(
      Object.values(files).filter((file) =>
        uploadingFiles
          .filter((el) => el.projectId === selectedProject)
          .every((uploadingFile) => uploadingFile.fileName !== file.fileName)
      )
    );
  }, [files, searchValue, uploadingFiles.length]);

  const sortedFiles = useMemo(() => {
    return sortProjectEntities(
      filteredFiles,
      filesTableSort.order,
      getFileSortCb(PROJECTS_FILES_HEADERS[filesTableSort.column])
    );
  }, [filteredFiles, filesTableSort]);

  const filesLen = useMemo(() => {
    return uploadingFiles.length + sortedFiles.length;
  }, [uploadingFiles.length, sortedFiles.length]);

  const selectedProjectName = useMemo(() => {
    return projects?.find((project) => project.projectId === selectedProject)?.name;
  }, [projects, selectedProject]);

  useEffect(() => {
    const currentFile = selectedFile ? files[selectedFile] : null;
    setSelected(currentFile ? currentFile.id : null);
  }, [selectedFile]);

  useEffect(() => {
    if (typeof selected !== 'string') return;
    if (uploadingFiles.findIndex((file) => file.id === selected) !== -1) return;
    dispatch(setRemovingInstance(null));
    setSelected(null);
  }, [uploadingFiles.length]);

  useEffect(() => {
    return () => clearSearchValue();
  }, [selectedProject]);

  const handleSelect = (file: ProjectFile) => {
    setSelected(file.id);
    dispatch(setRemovingInstance({ id: file.id, instance: 'file', modalOpen: false }));
    if (file.fileType !== FileType.audio) dispatch(setSelectedFile(file.id));
  };

  const handleSelectUpl = (file: UploadingFile) => {
    setSelected(file.id);
    dispatch(setRemovingInstance({ id: file.id, instance: 'file', modalOpen: false }));
  };

  const isDisabled = (id: FileId | FileUplId | null) => {
    return !id || removingInstance?.instance !== 'file';
  };

  return (
    <Section expanded={filesTableState}>
      <SectionHeader
        expanded={filesTableState}
        keyTableState="filesTableState"
        title="Media Files"
        projectName={selectedProjectName}
        empty={!Object.values(files).length}
        len={filesLen}
        tip="Drag and drop your files below (or use the Add Files button) to add them to the project"
      >
        <HeaderSearch
          searchValue={searchValue}
          updateSearchValue={updateSearchValue}
          clearSearchValue={clearSearchValue}
        />
        <PickerBase>
          <HeaderAction
            type="create"
            icon={<PlusIcon />}
            disabled={!selectedProject}
            data-intro={IntroKeys.addFiles}
            tip="Add files to this project (shift to select source)"
          />
        </PickerBase>
        <HeaderAction
          type="delete"
          icon={<TrashIcon />}
          onSubmit={() => {
            removingInstance?.instance === 'file' &&
              dispatch(setRemovingInstance({ ...removingInstance, modalOpen: true }));
          }}
          disabled={isDisabled(selected)}
          tip={isDisabled(selected) ? 'Select a file for deletion' : 'Delete file'}
        />
      </SectionHeader>
      <div className={styles.SectionBody}>
        <PickerDropZone>
          <PlaceholderFiles />
          {filesLen > 0 && (
            <ProjectsTable
              sortKey="filesTableSort"
              headers={PROJECTS_FILES_HEADERS}
              selectedRow={selectedFile ?? undefined}
            >
              {uploadingFiles
                .filter((el) => el.projectId === selectedProject)
                .map((file) => (
                  <tr
                    key={file.id}
                    onClick={() => handleSelectUpl(file)}
                    className={cm({
                      [styles.selected]: file.id === selected,
                      [styles.removing]:
                        removingInstance?.instance === 'file' &&
                        removingInstance?.modalOpen &&
                        removingInstance?.id === file.id,
                    })}
                  >
                    <td></td>
                    <td>{file.fileName}</td>
                    <Progress value={file.progress} />
                  </tr>
                ))}
              {sortedFiles?.map((file) => (
                <FileRow
                  key={file.id}
                  file={file}
                  onClick={() => handleSelect(file)}
                  role="project-file"
                  className={cm({
                    [styles.selected]: file.id === selected,
                    [styles.removing]:
                      removingInstance?.instance === 'file' &&
                      removingInstance?.modalOpen &&
                      removingInstance?.id === file.id,
                    [styles.error]: file.status === FileStatus.Error,
                  })}
                />
              ))}
            </ProjectsTable>
          )}
        </PickerDropZone>
      </div>
    </Section>
  );
};

/* C O M P O N E N T S */

const FileRow = (props: { file: ProjectFile } & React.HTMLAttributes<HTMLDivElement>) => {
  const { file, ...rest } = props;
  const queueFiles = useAppSelector((state) => state.app__filesUpload.queueFiles);
  const processingFiles = useAppSelector((state) => state.app__filesUpload.processingFiles);
  const statuses = useAppSelector((state) => state.app__files.statuses);

  return (
    <tr {...rest}>
      <ThumbFile el={file} />
      <td>{file.fileName}</td>
      {isFileStatusCompleted(statuses[file.id]) ? (
        <>
          <td>{mapFileStatusToLabel(statuses[file.id])}</td>
          <td>{FileType[file.fileType]}</td>
          <td>{prettyDuration(file.duration)}</td>
          <td>{prettyBytes(file.size)}</td>
          <td>{prettyResolution(file.width, file.height)}</td>
          <td>{prettyFrameRate(file.frameRate)}</td>
          <td>{prettyBitrate(file.origBitRate, file.bitRate)}</td>
          <td>
            <DateCalendar fileDate={file.mediaOriginDate} fileId={file.id} />
          </td>
          <td>{prettyDate(file.createdAt)}</td>
          <td>{prettyDate(file.accessedAt)}</td>
          <td>{prettyDate(file.updatedAt)}</td>
        </>
      ) : (
        <Progress status={statuses[file.id]} value={processingFiles[file.id]} positionInQueue={queueFiles[file.id]} />
      )}
    </tr>
  );
};

const Progress = (props: { status?: FileStatus; value?: number; positionInQueue?: number }) => {
  const { status, value, positionInQueue } = props;
  const helpMode = useAppSelector(selectHelpMode);
  return (
    <>
      <td colSpan={4}>
        <div className={styles.Table__fileStatus}>
          <Tooltip
            dynamicPosition
            showTooltip={helpMode}
            tip={
              status === FileStatus.PendingProcessing ? (
                <div>
                  The processing server is starting up — this may take 2-3 minutes and then processing will begin.
                  <br />
                  You'll receive an email when processing is complete, so you can step away.
                </div>
              ) : undefined
            }
          >
            <div className={styles.status}>{status ? mapFileStatusToLabel(status) : 'Uploading'}</div>
          </Tooltip>
          <Loader className={styles.spinner} />
          {!!positionInQueue && <div className={styles.queue}>Queue Position: {positionInQueue}</div>}
        </div>
      </td>
      <td colSpan={3}>
        {value && (
          <div className={styles.Table__fileProgress}>
            <div className={styles.progress}>
              <div style={{ width: value + '%' }}></div>
            </div>
            <div className={styles.value}>{value} %</div>
          </div>
        )}
      </td>
    </>
  );
};
